import * as React from "react";
import Dialog from "@mui/material/Dialog";
import { useMutation, useQuery } from "react-query";
import { fetchMFAPreference, setUpTOTP, updateMFAPreference, UpdateMFAPreferenceInput } from "aws-amplify/auth";
import { useUserSession } from "hooks/useUserSession";
import { DialogBody } from "components/DialogBody/DialogBody";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Switch from "@mui/material/Switch";
import { SetupTotp } from "./SetupTotp";
import { useDialogState } from "../../../../../../../hooks/useDialogState/useDialogState";
import { ConfirmationDialog } from "../../../../../../ConfirmationDialog/ConfirmationDialog";
import { useTranslation } from "react-i18next";

interface Props {
  onClose: () => void;
}

enum MfaScreen {
  SUMMARY = "SUMMARY",
  TOTP_SETUP = "TOTP_SETUP"
}

const MfaSettingsDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { username } = useUserSession();
  const [mfaScreen, setMfaScreen] = React.useState<MfaScreen>(MfaScreen.SUMMARY);
  const [isDisableTotpDialogOpen, openDisableTotpDialog, closeDisableTotpDialog] = useDialogState();
  const {
    data: mfaPreference,
    refetch: refetchUser,
    isLoading: isUserAndMfaLoading,
    error: userAndMfaError
  } = useQuery(["gerUserAndPreferredMfa", username], {
    queryFn: () => fetchMFAPreference(),
    cacheTime: 0
  });

  const {
    mutate: setupTotp,
    data: totpSetupDetails,
    isLoading: isSetupTotpLoading,
    error: setupTotpError
  } = useMutation(setUpTOTP);

  const {
    mutate: setPreferredMFA,
    isLoading: isSetPreferredMFALoading,
    error: setPreferredMFAError
  } = useMutation({
    mutationFn: (totp: UpdateMFAPreferenceInput["totp"]) => updateMFAPreference({ totp }),
    onSuccess: () => {
      refetchUser();
      closeDisableTotpDialog();
    }
  });

  const isTotpEnabled = mfaPreference?.preferred === "TOTP";

  return (
    <Dialog open={true} fullWidth={true} maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Typography variant="h6">{t("mfa.settings.title")}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogBody error={userAndMfaError || setupTotpError} isLoading={isUserAndMfaLoading || isSetupTotpLoading}>
        <DialogContent sx={{ minHeight: 300 }}>
          {mfaScreen === MfaScreen.SUMMARY && (
            <Stack direction="row" spacing={2} sx={{ px: 1, py: 2 }} justifyContent="space-between">
              <Stack>
                <Typography variant="body1">{t("mfa.settings.authenticatorApp")}</Typography>
                <Typography variant="caption">{t("mfa.settings.authenticatorAppDescription")}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={isTotpEnabled}
                  onChange={async (_, checked) => {
                    if (checked) {
                      setupTotp(undefined, { onSuccess: () => setMfaScreen(MfaScreen.TOTP_SETUP) });
                    } else {
                      openDisableTotpDialog();
                    }
                  }}
                />
                <Typography variant="body2">{isTotpEnabled ? "Enabled" : "Disabled"}</Typography>
              </Stack>
            </Stack>
          )}

          {mfaScreen === MfaScreen.TOTP_SETUP && totpSetupDetails && (
            <SetupTotp
              totpSecret={totpSetupDetails.sharedSecret}
              onSuccess={async () => {
                refetchUser();
                setMfaScreen(MfaScreen.SUMMARY);
              }}
              onClose={() => setMfaScreen(MfaScreen.SUMMARY)}
            />
          )}

          {isDisableTotpDialogOpen && (
            <ConfirmationDialog
              title="Disable Authenticator App MFA"
              content={<div>Are you sure you want to disable the Authenticator App MFA?</div>}
              onClose={closeDisableTotpDialog}
              onConfirm={() => setPreferredMFA("DISABLED")}
              isLoading={isSetPreferredMFALoading}
              error={setPreferredMFAError}
            />
          )}
        </DialogContent>
      </DialogBody>
    </Dialog>
  );
};

export { MfaSettingsDialog };
